import React, { useEffect, useState } from 'react'
import ModalComponent from './Modal'
import { studentDetailVerificationFields } from '../studentDetailVerification'

const StudentDetailConfirmationModal = ({
    showModal,
    setShowModal,
    confirmationState,
    setConfirmationState,
    fieldValues,
    onSubmit,
    loading,
}) => {
    const [showConfirmedMessage, setShowConfirmedMessage] = useState(false)
    const allChecked = studentDetailVerificationFields.every(({ key }) => confirmationState[key]?.checked)

    useEffect(() => {
        if (!showModal) return undefined
        const previousBodyOverflow = document.body.style.overflow
        const previousHtmlOverflow = document.documentElement.style.overflow
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousBodyOverflow
            document.documentElement.style.overflow = previousHtmlOverflow
        }
    }, [showModal])

    const handleConfirm = () => {
        setShowConfirmedMessage(true)
        window.setTimeout(() => setShowConfirmedMessage(false), 2200)
        onSubmit()
    }

    const toggleFields = (fieldKeys) => {
        const now = new Date().toISOString()
        setConfirmationState((currentState) => {
            const nextChecked = !fieldKeys.every((fieldKey) => currentState[fieldKey]?.checked)
            return fieldKeys.reduce((nextState, fieldKey) => ({
                ...nextState,
                [fieldKey]: {
                    checked: nextChecked,
                    checkedAt: nextChecked ? now : null,
                }
            }), currentState)
        })
    }

    const confirmationItems = [
        { id: 'firstName', keys: ['firstName'], label: 'First Name', value: fieldValues.firstName, className: 'confirmationHalf confirmationName' },
        { id: 'otherName', keys: ['otherName'], label: 'Other Name', value: fieldValues.otherName, className: 'confirmationHalf confirmationName' },
        { id: 'lastName', keys: ['lastName'], label: 'Last Name', value: fieldValues.lastName, className: 'confirmationFull' },
        { id: 'dob', keys: ['dob'], label: 'Date of Birth', value: fieldValues.dob, className: 'confirmationFull' },
        {
            id: 'postalStreet',
            keys: ['streetNumberPostal', 'streetNamePostal'],
            label: 'Postal Street Address',
            value: [fieldValues.streetNumberPostal, fieldValues.streetNamePostal].filter(Boolean).join(' '),
            className: 'confirmationFull',
        },
        { id: 'suburbPostal', keys: ['suburbPostal'], label: 'Postal Suburb', value: fieldValues.suburbPostal, className: 'confirmationThird' },
        { id: 'statePostal', keys: ['statePostal'], label: 'Postal State', value: fieldValues.statePostal, className: 'confirmationThird' },
        { id: 'postcodePostal', keys: ['postcodePostal'], label: 'Postcode', value: fieldValues.postcodePostal, className: 'confirmationThird' },
    ]

    const renderConfirmationItem = ({ id, keys, label, value, className }) => {
        const isChecked = keys.every((key) => confirmationState[key]?.checked)
        return (
            <div key={id} className={`detailConfirmationRow ${className}`} style={{ padding: '11px 14px' }}>
                <div>
                    <label style={{ fontWeight: 700 }}>{label}</label>
                    <p style={{ margin: '4px 0 0', wordBreak: 'break-word' }}>{value || '-'}</p>
                </div>
                <button
                    type='button'
                    className={`detailConfirmationTick modernVerificationTick ${isChecked ? 'checked' : ''}`}
                    onClick={() => toggleFields(keys)}
                    aria-label={`${isChecked ? 'Unconfirm' : 'Confirm'} ${label}`}
                >
                    ✓
                </button>
            </div>
        )
    }

    return (
        <ModalComponent
            showModal={showModal}
            setShowModal={setShowModal}
            contentClassName='studentConfirmationModal'
            overlayClassName='studentConfirmationOverlay'
            contentStyle={{ width: 'calc(100% - 48px)', maxWidth: '860px', height: 'auto', maxHeight: 'calc(100vh - 32px)', padding: 0, overflow: 'hidden', border: 'none', borderRadius: '14px', boxSizing: 'border-box' }}
        >
            {showConfirmedMessage && (
                <div className='studentConfirmationCenterMessage' role='status' aria-live='polite'>
                    <span aria-hidden='true'>✓</span>
                    <div>
                        <strong>Details confirmed</strong>
                        <small>Submitting your form...</small>
                    </div>
                </div>
            )}
            <header className='studentConfirmationHeader'>
                <div>
                    <h2>Confirm Your Details</h2>
                    <p>Review each detail and tick to confirm it is correct.</p>
                </div>
                <button type='button' className='studentConfirmationClose' onClick={() => setShowModal(false)}>Close</button>
            </header>

            <div className='detailConfirmationList' style={{ padding: '18px 28px' }}>
                <div className='confirmationNameRow' style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gridColumn: '1 / -1', gap: '10px', width: '100%' }}>
                    {confirmationItems.slice(0, 2).map(renderConfirmationItem)}
                </div>
                {confirmationItems.slice(2, 5).map(renderConfirmationItem)}
                <div className='confirmationPostalRow' style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gridColumn: '1 / -1', gap: '10px', width: '100%' }}>
                    {confirmationItems.slice(5).map(renderConfirmationItem)}
                </div>
            </div>

            <footer className='studentConfirmationActions'>
                <button type='button' className='studentConfirmationBack' onClick={() => setShowModal(false)} disabled={loading}>Back</button>
                <button
                    type='button'
                    className={`detailConfirmationSubmit ${(!allChecked || loading) ? 'disabled' : ''}`}
                    disabled={!allChecked || loading}
                    onClick={handleConfirm}
                >
                    {loading ? 'Confirming...' : 'Confirm'}
                </button>
            </footer>
        </ModalComponent>
    )
}

export default StudentDetailConfirmationModal
